import { useEffect, useRef, useState } from "react";
import { Sparkles, X } from "lucide-react";
import { CopilotWidget } from "@/components/CopilotWidget";
import { useData } from "@/contexts/DataContext";

const POS_KEY = "saathi.fab.pos";
const SIZE = 52;
const EDGE = 12;
const DROP_ZONE_SIZE = 64;
const DROP_ZONE_BOTTOM = 110;

type Pos = { x: number; y: number };

function loadPos(): Pos | null {
  try {
    const raw = localStorage.getItem(POS_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    if (typeof p.x === "number" && typeof p.y === "number") return p;
  } catch {
    // noop
  }
  return null;
}

function defaultPos(): Pos {
  if (typeof window === "undefined") return { x: 16, y: 100 };
  return {
    x: window.innerWidth - SIZE - 16,
    y: window.innerHeight - SIZE - 100,
  };
}

function clampPos(p: Pos): Pos {
  if (typeof window === "undefined") return p;
  const w = window.innerWidth;
  const h = window.innerHeight;
  return {
    x: Math.min(Math.max(EDGE, p.x), w - SIZE - EDGE),
    y: Math.min(Math.max(EDGE, p.y), h - SIZE - EDGE),
  };
}

function loadEnabled(): boolean {
  try {
    const v = localStorage.getItem("saathi.enabled");
    return v === null ? true : v === "true";
  } catch {
    return true;
  }
}

function isOverDropZone(clientX: number, clientY: number): boolean {
  if (typeof window === "undefined") return false;
  const w = window.innerWidth;
  const h = window.innerHeight;
  const cx = w / 2;
  const cy = h - DROP_ZONE_BOTTOM - DROP_ZONE_SIZE / 2;
  const dx = clientX - cx;
  const dy = clientY - cy;
  // Slightly bigger hit-test radius for forgiving drop
  const r = DROP_ZONE_SIZE / 2 + 16;
  return dx * dx + dy * dy <= r * r;
}

export function SaathiFab() {
  const { isPaid, openProUpgradeModal } = useData();
  const [open, setOpen] = useState(false);
  const [enabled, setEnabled] = useState<boolean>(loadEnabled);
  const [pos, setPos] = useState<Pos>(() => loadPos() || defaultPos());
  const [dragging, setDragging] = useState(false);
  const [overDrop, setOverDrop] = useState(false);
  const dragRef = useRef<{
    startX: number;
    startY: number;
    posX: number;
    posY: number;
    moved: boolean;
  } | null>(null);

  useEffect(() => {
    const handler = (e: Event) => {
      const v = (e as CustomEvent<boolean>).detail;
      setEnabled(typeof v === "boolean" ? v : loadEnabled());
    };
    window.addEventListener("saathi-enabled-change", handler);
    return () => window.removeEventListener("saathi-enabled-change", handler);
  }, []);

  useEffect(() => {
    const onResize = () => setPos((p) => clampPos(p));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  if (!enabled) return null;

  const startDrag = (clientX: number, clientY: number) => {
    dragRef.current = {
      startX: clientX,
      startY: clientY,
      posX: pos.x,
      posY: pos.y,
      moved: false,
    };
  };

  const moveDrag = (clientX: number, clientY: number) => {
    const d = dragRef.current;
    if (!d) return;
    const dx = clientX - d.startX;
    const dy = clientY - d.startY;
    if (!d.moved && Math.abs(dx) + Math.abs(dy) > 6) {
      d.moved = true;
      setDragging(true);
    }
    if (d.moved) {
      setPos(clampPos({ x: d.posX + dx, y: d.posY + dy }));
      // FAB center for drop-zone hit-test
      const cx = d.posX + dx + SIZE / 2;
      const cy = d.posY + dy + SIZE / 2;
      setOverDrop(isOverDropZone(cx, cy));
    }
  };

  const endDrag = (): { moved: boolean; dropped: boolean } => {
    const d = dragRef.current;
    dragRef.current = null;
    const moved = !!d?.moved;
    const dropped = moved && overDrop;
    setDragging(false);
    setOverDrop(false);
    if (dropped) {
      try {
        localStorage.setItem("saathi.enabled", "false");
      } catch {
        // noop
      }
      setEnabled(false);
      window.dispatchEvent(new CustomEvent("saathi-enabled-change", { detail: false }));
    } else if (moved) {
      try {
        localStorage.setItem(POS_KEY, JSON.stringify(pos));
      } catch {
        // noop
      }
    }
    return { moved, dropped };
  };

  if (open) {
    return <CopilotWidget open={open} onOpenChange={setOpen} />;
  }

  return (
    <>
      {/* Drop-to-disable zone — circular X overlay */}
      {dragging && (
        <div
          className={`fixed z-30 left-1/2 -translate-x-1/2 rounded-full flex items-center justify-center transition-all border-2 ${
            overDrop
              ? "bg-red-500/85 border-white scale-125 shadow-2xl shadow-red-500/50"
              : "bg-slate-900/45 dark:bg-slate-800/50 border-white/30 backdrop-blur-md"
          }`}
          style={{
            bottom: DROP_ZONE_BOTTOM,
            width: DROP_ZONE_SIZE,
            height: DROP_ZONE_SIZE,
          }}
        >
          <X className="w-7 h-7 text-white" strokeWidth={2.5} />
        </div>
      )}

      <button
        aria-label="Open Saathi AI"
        onPointerDown={(e) => {
          (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
          startDrag(e.clientX, e.clientY);
        }}
        onPointerMove={(e) => moveDrag(e.clientX, e.clientY)}
        onPointerUp={(e) => {
          const r = endDrag();
          if (!r.moved) {
            // Saathi AI is a Premium perk; free users get the upgrade offer.
            if (isPaid) setOpen(true);
            else openProUpgradeModal();
          }
          (e.target as HTMLElement).releasePointerCapture?.(e.pointerId);
        }}
        onPointerCancel={() => endDrag()}
        className={`group fixed z-40 select-none touch-none ${dragging ? "scale-110 cursor-grabbing" : "active:scale-95 cursor-grab"} transition-transform`}
        style={{
          left: pos.x,
          top: pos.y,
          width: SIZE,
          height: SIZE,
          touchAction: "none",
        }}
        data-testid="fab-saathi"
      >
        {/* Conic gradient ring */}
        <span
          className="absolute -inset-[2px] rounded-2xl opacity-80"
          style={{
            background:
              "conic-gradient(from 0deg, #8B1A1A, #C72525, #FF6B35, #C72525, #8B1A1A)",
            animation: "spin 5s linear infinite",
          }}
        />
        <div
          className="relative w-full h-full rounded-2xl flex items-center justify-center shadow-lg shadow-[#8B1A1A]/40 overflow-hidden"
          style={{
            background:
              "radial-gradient(circle at 30% 25%, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0) 40%), linear-gradient(135deg, #8B1A1A 0%, #631212 100%)",
          }}
        >
          <Sparkles
            className="w-5 h-5 text-white animate-[pulse_2.5s_ease-in-out_infinite]"
            strokeWidth={2.25}
          />
        </div>
        {/* Status dot */}
        <span className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-400 border-2 border-white shadow" />
      </button>
    </>
  );
}
